import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, CircularProgress, Button, Grid,
  List, ListItem, ListItemButton, ListItemText, Divider, Collapse, Link
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArticleIcon from '@mui/icons-material/Article';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import GroupsIcon from '@mui/icons-material/Groups';
import GavelIcon from '@mui/icons-material/Gavel';
import { getCommitteeReport } from '../services/api';
import CopyLinkButton from './CopyLinkButton';
import ShareButton from './ShareButton';

// Component for displaying a chapter
function ChapterContent({ chapter, isFirst }) {
  return (
    <Box
      id={`chapter-${chapter.number || chapter.title.toLowerCase().replace(/\s+/g, '-')}`}
      sx={{
        scrollMarginTop: '80px',
        mb: 4,
        pb: 3,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' }
      }}
    >
      {/* Chapter header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <ArticleIcon sx={{ color: '#1565c0', fontSize: isFirst ? 28 : 24 }} />
        <Typography variant={isFirst ? 'h5' : 'h6'} sx={{ fontWeight: 600, color: '#1565c0' }}>
          {chapter.number ? `Chapter ${chapter.number}` : chapter.title}
        </Typography>
      </Box>
      {chapter.number && (
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
          {chapter.title}
        </Typography>
      )}
      {chapter.start_page && (
        <Chip label={`Page ${chapter.start_page}`} size="small" variant="outlined" sx={{ mb: 2 }} />
      )}

      {/* Content */}
      <Box sx={{
        '& p': { mb: 2, lineHeight: 1.8 },
        whiteSpace: 'pre-wrap',
        fontSize: '0.95rem',
        lineHeight: 1.8,
        color: '#333'
      }}>
        {chapter.content?.split('\n').map((para, idx) => (
          para.trim() && (
            <Typography key={idx} paragraph sx={{ mb: 1.5 }}>
              {para}
            </Typography>
          )
        ))}
      </Box>
    </Box>
  );
}

// Component for displaying recommendations
function RecommendationsList({ recommendations }) {
  if (!recommendations || recommendations.length === 0) return null;

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <LightbulbIcon sx={{ color: '#f9a825', fontSize: 24 }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Recommendations ({recommendations.length})
        </Typography>
      </Box>
      <List sx={{ bgcolor: '#fffde7', borderRadius: 1, p: 2 }}>
        {recommendations.map((rec, idx) => (
          <ListItem key={idx} sx={{ display: 'block', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#f9a825', mb: 0.5 }}>
              Recommendation {rec.number}
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
              {rec.text}
            </Typography>
          </ListItem>
        ))}
      </List>
    </Box>
  );
}

export default function CommitteeReportDetail() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedToc, setExpandedToc] = useState(true);
  const [expandedRecs, setExpandedRecs] = useState(true);
  const [showAllToc, setShowAllToc] = useState(false);
  const [showAllRecs, setShowAllRecs] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getCommitteeReport(reportId);
        setReport(data);
      } catch (err) {
        console.error('Error fetching report:', err);
        setError('Report not found');
      }
      setLoading(false);
    };

    fetchReport();
  }, [reportId]);

  const scrollToChapter = (chapterNum, chapterTitle) => {
    const id = chapterNum ? `chapter-${chapterNum}` : `chapter-${chapterTitle.toLowerCase().replace(/\s+/g, '-')}`;
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Senate': return 'error';
      case 'House': return 'success';
      case 'Joint': return 'primary';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/committee-reports')} sx={{ mb: 2 }}>
          Back to Search
        </Button>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      </Box>
    );
  }

  if (!report) return null;

  const chapters = report.chapters || [];
  const toc = report.toc || [];
  const recommendations = report.recommendations || [];

  return (
    <Box sx={{ p: 3 }}>
      {/* Navigation bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/committee-reports')} sx={{ color: 'text.secondary' }}>
          Back to Search
        </Button>
        <CopyLinkButton size="small" />
        <ShareButton size="small" />
      </Box>

      <Grid container spacing={3}>
        {/* Left sidebar - TOC */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, position: 'sticky', top: 80, maxHeight: 'calc(100vh - 120px)', overflow: 'auto' }}>
            {/* Report metadata */}
            <Box sx={{ mb: 3 }}>
              <Chip
                label={report.committee_type}
                size="small"
                color={getTypeColor(report.committee_type)}
                sx={{ mb: 1 }}
              />
              {report.report_date && (
                <Typography variant="body2" color="text.secondary">
                  {formatDate(report.report_date)}
                </Typography>
              )}
              {report.page_count && (
                <Typography variant="body2" color="text.secondary">
                  {report.page_count} pages
                </Typography>
              )}
            </Box>

            {/* External links */}
            <Box sx={{ mb: 3, display: 'flex', gap: 1 }}>
              {report.pdf_url && (
                <Link
                  href={report.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}
                >
                  <PictureAsPdfIcon fontSize="small" /> PDF
                </Link>
              )}
              {report.display_url && (
                <Link
                  href={report.display_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}
                >
                  <OpenInNewIcon fontSize="small" /> ParlInfo
                </Link>
              )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Table of Contents */}
            {toc.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <ListItemButton onClick={() => setExpandedToc(!expandedToc)} sx={{ px: 0 }}>
                  <ListItemText primary="Table of Contents" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }} />
                  {expandedToc ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={expandedToc}>
                  <List dense sx={{ pl: 1 }}>
                    {(showAllToc ? toc : toc.slice(0, 30)).map((item, idx) => (
                      <ListItem key={idx} disablePadding>
                        <ListItemButton
                          onClick={() => scrollToChapter(null, item.title)}
                          sx={{ py: 0.5, pl: (item.level || 1) * 1.5 }}
                        >
                          <ListItemText
                            primary={item.title}
                            primaryTypographyProps={{ fontSize: '0.8rem' }}
                            secondary={item.page ? `p. ${item.page}` : null}
                            secondaryTypographyProps={{ fontSize: '0.7rem' }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                    {toc.length > 30 && (
                      <ListItem disablePadding>
                        <ListItemButton onClick={() => setShowAllToc(!showAllToc)} sx={{ py: 0.5, pl: 2 }}>
                          <ListItemText
                            primary={showAllToc ? 'Show fewer items' : `+ ${toc.length - 30} more items`}
                            primaryTypographyProps={{ fontSize: '0.8rem', color: 'ledger.accentText', fontStyle: 'italic' }}
                          />
                        </ListItemButton>
                      </ListItem>
                    )}
                  </List>
                </Collapse>
              </Box>
            )}

            {/* Chapters navigation */}
            {chapters.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Chapters</Typography>
                <List dense>
                  {chapters.filter(ch => ch.number).map((chapter, idx) => (
                    <ListItem key={idx} disablePadding>
                      <ListItemButton onClick={() => scrollToChapter(chapter.number, chapter.title)} sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={`Ch. ${chapter.number}: ${chapter.title?.slice(0, 40)}${chapter.title?.length > 40 ? '...' : ''}`}
                          primaryTypographyProps={{ fontSize: '0.8rem' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Recommendations quick nav */}
            {recommendations.length > 0 && (
              <Box>
                <ListItemButton onClick={() => setExpandedRecs(!expandedRecs)} sx={{ px: 0 }}>
                  <LightbulbIcon sx={{ color: '#f9a825', fontSize: 20, mr: 1 }} />
                  <ListItemText
                    primary={`Recommendations (${recommendations.length})`}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                  />
                  {expandedRecs ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={expandedRecs}>
                  <List dense sx={{ pl: 1 }}>
                    {(showAllRecs ? recommendations : recommendations.slice(0, 10)).map((rec, idx) => (
                      <ListItem key={idx} disablePadding>
                        <ListItemText
                          primary={`Rec ${rec.number}: ${rec.text?.slice(0, 50)}...`}
                          primaryTypographyProps={{ fontSize: '0.75rem' }}
                        />
                      </ListItem>
                    ))}
                    {recommendations.length > 10 && (
                      <ListItem disablePadding>
                        <ListItemButton onClick={() => setShowAllRecs(!showAllRecs)} sx={{ py: 0.5, pl: 1 }}>
                          <ListItemText
                            primary={showAllRecs ? 'Show fewer' : `+ ${recommendations.length - 10} more`}
                            primaryTypographyProps={{ fontSize: '0.75rem', color: 'ledger.accentText', fontStyle: 'italic' }}
                          />
                        </ListItemButton>
                      </ListItem>
                    )}
                  </List>
                </Collapse>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Main content */}
        <Grid item xs={12} md={9}>
          <Paper sx={{ p: 3 }} ref={contentRef}>
            {/* Report header */}
            <Box sx={{ mb: 4, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
                {report.title}
              </Typography>
              {report.committee_name && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <GroupsIcon sx={{ color: 'text.secondary' }} />
                  <Typography variant="subtitle1" color="text.secondary">
                    {report.committee_name}
                  </Typography>
                </Box>
              )}
              {report.subject && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {report.subject}
                </Typography>
              )}
            </Box>

            {/* Members section */}
            {report.members && (
              <Box sx={{ mb: 4, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Committee Members</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {report.members}
                </Typography>
              </Box>
            )}

            {/* Terms of Reference */}
            {report.terms_of_reference && (
              <Box sx={{ mb: 4, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <GavelIcon sx={{ color: '#1565c0' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Terms of Reference</Typography>
                </Box>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {report.terms_of_reference}
                </Typography>
              </Box>
            )}

            {/* Recommendations summary */}
            <RecommendationsList recommendations={recommendations} />

            {/* Content HTML if available */}
            {report.content_html ? (
              <Box
                sx={{
                  '& article': { width: '100%' },
                  '& header h1': { display: 'none' },
                  '& .committee-name': { display: 'none' },
                  '& section': { mb: 4 },
                  '& h2': { fontSize: '1.5rem', fontWeight: 600, color: '#1565c0', mb: 2 },
                  '& p': { mb: 1.5, lineHeight: 1.8 },
                  '& .numbered-para': { pl: 2 },
                  '& .para-num': { fontWeight: 600, color: '#666', mr: 1 },
                  '& .recommendation': { bgcolor: '#fffde7', p: 1, borderRadius: 1, my: 1 },
                }}
                dangerouslySetInnerHTML={{ __html: report.content_html }}
              />
            ) : (
              /* Chapters */
              chapters.length > 0 && (
                <Box>
                  {chapters.map((chapter, idx) => (
                    <ChapterContent key={idx} chapter={chapter} isFirst={idx === 0} />
                  ))}
                </Box>
              )
            )}

            {/* Fallback when no extracted content */}
            {!report.content_html && chapters.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  Full content not yet extracted for this report.
                </Typography>
                {report.pdf_url && (
                  <Button
                    variant="contained"
                    href={report.pdf_url}
                    target="_blank"
                    startIcon={<PictureAsPdfIcon />}
                  >
                    View PDF
                  </Button>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
